import React from 'react';
import { Info, Clock, Calendar, FileText, Book, AlertTriangle, Moon, DollarSign } from 'lucide-react';

/**
 * Компонент с информацией о расчете рабочего времени и трудовом законодательстве РФ
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-cyan-500" />
        Рабочее время в России: правила и нормы
      </h2>
      
      <div className="prose prose-cyan dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Book className="w-5 h-5 text-cyan-500" />
          Нормативная база
        </h3>
        <p>
          Рабочее время в Российской Федерации регламентируется <strong>Трудовым кодексом РФ</strong>, 
          в частности главами 15-16 (статьи 91-105). Эти положения устанавливают правовые нормы 
          продолжительности и распределения рабочего времени, определяют различные режимы работы, 
          а также регулируют оплату труда в зависимости от отработанного времени.
        </p>

        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-cyan-500" />
          Стандартная продолжительность рабочего времени
        </h3>
        <p>
          Согласно статье 91 ТК РФ, нормальная продолжительность рабочего времени составляет <strong>40 часов в неделю</strong>. 
          При этом для некоторых категорий работников устанавливается сокращенная продолжительность рабочего времени:
        </p>
        <ul>
          <li>Для работников в возрасте до 16 лет – не более 24 часов в неделю</li>
          <li>Для работников в возрасте от 16 до 18 лет – не более 35 часов в неделю</li>
          <li>Для работников, являющихся инвалидами I или II группы – не более 35 часов в неделю</li>
          <li>Для работников, занятых на работах с вредными/опасными условиями труда – не более 36 часов в неделю</li>
          <li>Для медицинских работников – не более 39 часов в неделю</li>
          <li>Для педагогических работников – не более 36 часов в неделю</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-cyan-500" />
          Режимы рабочего времени
        </h3>
        <p>
          Трудовой кодекс РФ предусматривает различные режимы рабочего времени:
        </p>
        <ul>
          <li><strong>Пятидневная рабочая неделя</strong> с двумя выходными (обычно суббота и воскресенье)</li>
          <li><strong>Шестидневная рабочая неделя</strong> с одним выходным</li>
          <li><strong>Работа в режиме гибкого рабочего времени</strong>, при которой работник может сам регулировать начало, окончание и продолжительность рабочего дня</li>
          <li><strong>Сменная работа</strong>, при которой работники сменяют друг друга в соответствии с графиком сменности</li>
          <li><strong>Суммированный учет рабочего времени</strong>, когда продолжительность рабочего времени за учетный период не превышает нормального числа рабочих часов</li>
          <li><strong>Разделение рабочего дня на части</strong>, что позволяет распределить рабочее время с учетом характера деятельности</li>
        </ul>

        <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-100 dark:border-cyan-800 my-6">
          <h4 className="text-cyan-700 dark:text-cyan-300 font-medium">Производственный календарь</h4>
          <p className="text-cyan-600 dark:text-cyan-400 text-sm mb-0">
            В России ежегодно утверждается производственный календарь, который учитывает все праздничные дни и переносы выходных дней. 
            Он используется для планирования рабочего времени и расчета заработной платы. Правительство РФ может переносить 
            выходные дни на другие дни в целях рационального использования нерабочих праздничных дней.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Moon className="w-5 h-5 text-indigo-500" />
          Работа в ночное время
        </h3>
        <p>
          Согласно статье 96 ТК РФ, <strong>ночным считается время с 22 часов до 6 часов</strong>. 
          Продолжительность работы в ночное время сокращается на один час без последующей отработки.
          Работа в ночное время оплачивается в повышенном размере, устанавливаемом коллективным договором или локальными актами, 
          но не ниже размеров, установленных трудовым законодательством (обычно не менее чем на 20% выше).
        </p>
        <p>
          К работе в ночное время не допускаются:
        </p>
        <ul>
          <li>Беременные женщины</li>
          <li>Работники, не достигшие возраста 18 лет (за исключением некоторых категорий)</li>
          <li>Другие категории работников в соответствии с ТК РФ и иными федеральными законами</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          Сверхурочная работа
        </h3>
        <p>
          Сверхурочная работа – это работа, выполняемая по инициативе работодателя за пределами установленной 
          продолжительности рабочего времени. Согласно статье 99 ТК РФ, привлечение к сверхурочной работе 
          допускается с письменного согласия работника и с учетом мнения выборного органа первичной профсоюзной организации.
        </p>
        <p>
          Продолжительность сверхурочной работы не должна превышать для каждого работника <strong>4 часов в 
          течение двух дней подряд и 120 часов в год</strong>.
        </p>
        <p>
          Оплата сверхурочной работы производится следующим образом:
        </p>
        <ul>
          <li>Первые два часа – не менее чем в полуторном размере</li>
          <li>Последующие часы – не менее чем в двойном размере</li>
        </ul>
        <p>
          По желанию работника сверхурочная работа вместо повышенной оплаты может компенсироваться 
          предоставлением дополнительного времени отдыха, но не менее времени, отработанного сверхурочно.
        </p>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-red-500" />
          Работа в выходные и нерабочие праздничные дни
        </h3>
        <p>
          Согласно статье 113 ТК РФ, работа в выходные и нерабочие праздничные дни запрещается, за исключением 
          случаев, предусмотренных трудовым законодательством. Привлечение к работе в эти дни производится 
          с письменного согласия работника и с учетом мнения выборного органа первичной профсоюзной организации.
        </p>
        <p>
          Оплата работы в выходной или нерабочий праздничный день производится согласно статье 153 ТК РФ:
        </p>
        <ul>
          <li>Не менее чем в двойном размере</li>
          <li>По желанию работника, работавшего в выходной или нерабочий праздничный день, ему может быть предоставлен другой день отдыха</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Оплата труда
        </h3>
        <p>
          Особенности оплаты труда в зависимости от режима работы:
        </p>
        <ul>
          <li><strong>Повременная система</strong> – оплата труда производится за фактически отработанное время</li>
          <li><strong>Сдельная система</strong> – оплата производится за количество и качество выполненной работы</li>
          <li><strong>Смешанная система</strong> – комбинация повременной и сдельной систем</li>
        </ul>
        <p>
          При работе в особых условиях предусмотрена повышенная оплата труда:
        </p>
        <ul>
          <li>За работу в ночное время – не менее 20% часовой тарифной ставки за каждый час работы</li>
          <li>За сверхурочную работу – первые два часа в полуторном размере, последующие в двойном</li>
          <li>За работу в выходные и праздничные дни – не менее чем в двойном размере</li>
          <li>За работу во вредных или опасных условиях труда – не менее 4% тарифной ставки</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Учет рабочего времени
          </h4>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            Работодатель обязан вести учет времени, фактически отработанного каждым работником. 
            С 2023 года многие компании начали переходить на электронный документооборот в сфере трудовых отношений, 
            в том числе и на электронный учет рабочего времени, что регулируется главой 49.1 ТК РФ.
          </p>
        </div>

        <h3>Нормы труда и сменная работа</h3>
        <p>
          В соответствии с ТК РФ:
        </p>
        <ul>
          <li>При работе на условиях неполного рабочего времени оплата труда производится пропорционально отработанному времени или в зависимости от выполненного объема работ</li>
          <li>При сменной работе применяется суммированный учет рабочего времени с учетным периодом не более года</li>
          <li>Графики сменности доводятся до сведения работников не позднее чем за месяц до введения их в действие</li>
        </ul>

        <h3>Перерывы в работе и отдых</h3>
        <p>
          Согласно ст. 108 ТК РФ:
        </p>
        <ol>
          <li>В течение рабочего дня работнику должен быть предоставлен перерыв для отдыха и питания продолжительностью не более 2 часов и не менее 30 минут, который в рабочее время не включается</li>
          <li>Время предоставления перерыва и его конкретная продолжительность устанавливаются правилами внутреннего трудового распорядка</li>
          <li>На работах, где по условиям производства предоставление перерыва невозможно, работодатель обязан обеспечить возможность отдыха и приема пищи в рабочее время</li>
        </ol>

        <p className="font-semibold mt-6">
          Наш калькулятор рабочего времени поможет вам корректно рассчитать количество рабочих дней и часов, 
          с учетом всех особенностей российского трудового законодательства, производственного календаря и 
          различных режимов рабочего времени.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;